import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Clock, User, MapPin, CheckCircle, XCircle, Calendar, DollarSign, Hash, AlertCircle, RotateCcw, UserCheck, FileText, Package, ClipboardList, History } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { getStatusColor, getStatusLabel, getTagColor, formatTime, formatDateTime } from '../utils/helpers';

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL + '/api'
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const AppointmentCard = ({
  appointment,
  user,
  onCheckIn,
  onCheckOut,
  onUndoCheckIn,
  onCancel,
  onConfirm,
  onMarkPackage,
  onUndoPackage,
  onIssueToken,
  onNoShow,
  onReschedule,
  onFollowUp,
  onAddNotes,
  onSendDirections,
  onViewPatient,
  onViewLinkedAppointment,
  isHighlighted
}) => {
  const isAdmin = user?.role === 'Admin';
  const isDelayed = appointment.lateness_minutes && appointment.lateness_minutes > 0;
  const isCheckedIn = appointment.status === 'checked_in';
  
  // Activity log state
  const [activityLogOpen, setActivityLogOpen] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const fetchActivityLogs = async () => {
    setLoadingLogs(true);
    try {
      const response = await api.get(`/appointments/${appointment.id}/activity-log`);
      setActivityLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch activity logs:', error);
    }
    setLoadingLogs(false);
  };

  const openActivityLog = () => {
    setActivityLogOpen(true);
    fetchActivityLogs();
  };

  return (
    <>
    {/* Activity Log Modal */}
    <Dialog open={activityLogOpen} onOpenChange={setActivityLogOpen}>
      <DialogContent className="max-w-md max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-purple-600" />
            Activity Log - {appointment.ref_number || appointment.id.slice(0, 8)}
          </DialogTitle>
        </DialogHeader>
        <div className="text-sm text-gray-600 mb-3">
          {appointment.patient_name} • {appointment.service_name}
        </div>
        {loadingLogs ? (
          <div className="text-center py-4 text-gray-500">Loading...</div>
        ) : activityLogs.length === 0 ? (
          <div className="text-center py-4 text-gray-500">No activity recorded</div>
        ) : (
          <div className="space-y-3">
            {activityLogs.map((log, index) => (
              <div key={index} className="border-l-2 border-purple-300 pl-3 py-1">
                <div className="text-xs text-gray-500">
                  {new Date(log.timestamp).toLocaleString('en-IN', { 
                    day: '2-digit', month: 'short', year: 'numeric',
                    hour: '2-digit', minute: '2-digit'
                  })}
                </div>
                <div className="text-sm font-medium text-gray-800">
                  {log.action_type?.replace(/_/g, ' ')}
                </div>
                <div className="text-xs text-gray-600">{log.description}</div>
                <div className="text-xs text-purple-600">by {log.user_name || 'System'}</div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
    <div 
      id={`appointment-${appointment.id}`}
      data-appointment-id={appointment.id}
      className={`appointment-card bg-white rounded-xl border-2 p-5 transition-all duration-300 ${isDelayed ? 'delayed-checkin' : ''} ${isHighlighted ? 'ring-4 ring-purple-500 shadow-lg scale-[1.02]' : ''} ${getStatusColor(appointment.status).replace('bg-', 'border-').split(' ')[0]}`}
      data-testid={`appointment-card-${appointment.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          {/* Reference Number & Activity Log */}
          <div className="flex items-center gap-2 mb-1">
            {appointment.ref_number && (
              <span className="text-xs font-mono bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                {appointment.ref_number}
              </span>
            )}
            <button
              onClick={openActivityLog}
              className="text-gray-400 hover:text-purple-600 transition"
              title="View Activity Log"
            >
              <History className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <h3 
              className="text-lg font-bold text-gray-900 cursor-pointer hover:text-purple-600 transition"
              onClick={onViewPatient}
              data-testid={`patient-name-${appointment.id}`}
            >
              {appointment.patient_name}
            </h3>
            {appointment.patient_tags && appointment.patient_tags.map(tag => (
              <Badge key={tag} className={`${getTagColor(tag)} text-xs px-2 py-0.5`}>
                {tag}
              </Badge>
            ))}
          </div>
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <span className="flex items-center gap-1">
              <User className="w-4 h-4" />
              {appointment.patient_phone}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {appointment.service_name}
            </span>
            {appointment.scheduled_time && (
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {formatTime(appointment.scheduled_time)}
              </span>
            )}
          </div>
        </div>
        
        <div className="flex flex-col gap-2 items-end">
          <Badge className={`${getStatusColor(appointment.status)} px-3 py-1 text-xs font-semibold`}>
            {getStatusLabel(appointment.status)}
          </Badge>
          {appointment.is_paid ? (
            <Badge className="bg-green-100 text-green-700 px-3 py-1 text-xs font-semibold">
              ✓ Paid {isAdmin && appointment.payment_amount > 0 && `₹${appointment.payment_amount.toLocaleString()}`}
            </Badge>
          ) : (
            <Badge className="bg-amber-100 text-amber-700 px-3 py-1 text-xs font-semibold">
              ₹ Unpaid
            </Badge>
          )}
          {appointment.is_package && (
            <Badge className="bg-indigo-100 text-indigo-700 px-3 py-1 text-xs font-semibold">
              Package
            </Badge>
          )}
        </div>
      </div>

      {/* Token or Time Info */}
      {appointment.service_type === 'queue_based' && (
        <div className="mb-4 p-3 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg">
          {appointment.token_number ? (
            <>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Token Number:</span>
                <span className="text-2xl font-bold text-emerald-700 font-mono">{appointment.token_number}</span>
              </div>
              {!appointment.is_paid && (
                <div className="flex items-center justify-center text-amber-600 mt-2 pt-2 border-t border-emerald-200">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  <span className="text-xs font-medium">Payment pending</span>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center justify-center text-amber-600">
                <AlertCircle className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">Scheduled - Token not issued</span>
              </div>
              {!appointment.is_paid && (
                <div className="flex items-center justify-center text-amber-600 mt-2 pt-2 border-t border-emerald-200">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  <span className="text-xs font-medium">Payment pending</span>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Job Card Progress - for time-based services */}
      {appointment.job_card_status && (
        <div className={`mb-4 p-3 rounded-lg border ${
          appointment.job_card_status === 'completed' 
            ? 'bg-green-50 border-green-200' 
            : appointment.job_card_status === 'in_progress'
              ? 'bg-blue-50 border-blue-200'
              : appointment.job_card_status === 'cancelled'
                ? 'bg-red-50 border-red-200'
                : 'bg-orange-50 border-orange-200'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ClipboardList className={`w-4 h-4 ${
                appointment.job_card_status === 'completed' ? 'text-green-600' :
                appointment.job_card_status === 'in_progress' ? 'text-blue-600' :
                appointment.job_card_status === 'cancelled' ? 'text-red-600' :
                'text-orange-600'
              }`} />
              <span className="text-sm font-medium text-gray-700">Job Card:</span>
              <Badge className={`text-xs ${
                appointment.job_card_status === 'completed' ? 'bg-green-100 text-green-700' :
                appointment.job_card_status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                appointment.job_card_status === 'cancelled' ? 'bg-red-100 text-red-700' :
                'bg-orange-100 text-orange-700'
              }`}>
                {appointment.job_card_status.replace('_', ' ')}
              </Badge>
            </div>
            <div className="flex items-center gap-3">
              {isAdmin && appointment.payment_amount > 0 && (
                <span className="text-sm font-bold text-green-700">
                  ₹{appointment.payment_amount.toLocaleString()}
                </span>
              )}
              {appointment.job_card_progress && (
                <span className={`text-sm font-bold ${
                  appointment.job_card_status === 'completed' ? 'text-green-700' :
                  appointment.job_card_status === 'in_progress' ? 'text-blue-700' :
                  'text-orange-700'
                }`}>
                  {appointment.job_card_progress}
                </span>
              )}
            </div>
          </div>
          {appointment.job_card_therapists && appointment.job_card_therapists.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-200 flex items-center gap-2 text-xs text-gray-600">
              <User className="w-3 h-3" />
              <span>{appointment.job_card_therapists.join(', ')}</span>
            </div>
          )}
        </div>
      )}

      {/* Forwarded Info - Original appointment that was rescheduled */}
      {appointment.status === 'forwarded' && appointment.rescheduled_to_appointment_id && (
        <div className="mb-4 p-3 bg-amber-50 border-l-4 border-amber-400 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-amber-800">Forwarded to new appointment</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onViewLinkedAppointment(appointment.rescheduled_to_appointment_id)}
              data-testid={`view-forwarded-${appointment.id}`}
              className="text-xs border-amber-300 text-amber-600 hover:bg-amber-100"
            >
              View New Appointment →
            </Button>
          </div>
        </div>
      )}

      {/* Rescheduled Badge - New appointment created from reschedule */}
      {appointment.status === 'rescheduled' && appointment.original_appointment_id && (
        <div className="mb-4 p-3 bg-indigo-50 border-l-4 border-indigo-400 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-indigo-800">Rescheduled from original appointment</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onViewLinkedAppointment(appointment.original_appointment_id)}
              data-testid={`view-original-${appointment.id}`}
              className="text-xs border-indigo-300 text-indigo-600 hover:bg-indigo-100"
            >
              View Original Appointment →
            </Button>
          </div>
        </div>
      )}

      {/* Check-in/out info */}
      {(appointment.check_in_time || appointment.check_out_time) && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg space-y-2 text-sm">
          {appointment.check_in_time && (
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Check-in:</span>
              <span className="font-medium text-emerald-700">{formatDateTime(appointment.check_in_time)}</span>
            </div>
          )}
          {appointment.lateness_minutes > 0 && (
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Lateness:</span>
              <span className="font-medium text-amber-600">{appointment.lateness_minutes} min</span>
            </div>
          )}
          {appointment.check_out_time && (
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Check-out:</span>
              <span className="font-medium text-purple-700">{formatDateTime(appointment.check_out_time)}</span>
            </div>
          )}
          {appointment.exposure_time_minutes && (
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Exposure Time:</span>
              <span className="font-medium text-purple-700">{Math.round(appointment.exposure_time_minutes)} min</span>
            </div>
          )}
        </div>
      )}

      {/* Notes/Remarks */}
      {appointment.remarks && (
        <div className="mb-4 p-3 bg-blue-50 border-l-4 border-blue-400 rounded-lg">
          <div className="flex items-start gap-2">
            <FileText className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-xs font-semibold text-blue-800 mb-1">Notes:</p>
              <p className="text-sm text-blue-900 whitespace-pre-wrap">{appointment.remarks}</p>
            </div>
          </div>
        </div>
      )}

      {/* Payment Status */}
      {!appointment.is_paid && appointment.service_type === 'queue_based' && (
        <div className="mb-4 p-2 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-sm text-amber-800">
          <AlertCircle className="w-4 h-4" />
          <span>Payment pending</span>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2">
        {(appointment.status === 'scheduled' || appointment.status === 'rescheduled') && (
          <Button 
            size="sm" 
            onClick={onConfirm}
            data-testid={`confirm-button-${appointment.id}`}
            className="bg-teal-600 hover:bg-teal-700 text-white rounded-lg"
          >
            <CheckCircle className="w-4 h-4 mr-1" />
            Confirm
          </Button>
        )}
        
        {(appointment.status === 'scheduled' || appointment.status === 'confirmed' || appointment.status === 'rescheduled') && (
          <>
            <Button 
              size="sm" 
              onClick={onCheckIn}
              data-testid={`checkin-button-${appointment.id}`}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              Check In
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              onClick={onReschedule}
              data-testid={`reschedule-button-${appointment.id}`}
              className="border-indigo-300 text-indigo-600 hover:bg-indigo-50 rounded-lg"
            >
              <Calendar className="w-4 h-4 mr-1" />
              Reschedule
            </Button>
          </>
        )}
        
        {appointment.status === 'checked_in' && (
          <>
            <Button 
              size="sm" 
              onClick={onCheckOut}
              data-testid={`checkout-button-${appointment.id}`}
              className="bg-purple-600 hover:bg-purple-700 text-white rounded-lg"
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              Check Out
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              onClick={onUndoCheckIn}
              data-testid={`undo-checkin-button-${appointment.id}`}
              className="border-amber-300 text-amber-600 hover:bg-amber-50 rounded-lg"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Undo Check-in
            </Button>
            {!appointment.follow_up_appointment_id && (
              <Button 
                size="sm" 
                onClick={onFollowUp}
                data-testid={`followup-button-${appointment.id}`}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
              >
                <UserCheck className="w-4 h-4 mr-1" />
                Create Follow-up
              </Button>
            )}
            {appointment.follow_up_appointment_id && (
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => onViewLinkedAppointment(appointment.follow_up_appointment_id)}
                data-testid={`view-followup-${appointment.id}`}
                className="border-emerald-300 text-emerald-600 hover:bg-emerald-50 rounded-lg"
              >
                <UserCheck className="w-4 h-4 mr-1" />
                Follow-up Created →
              </Button>
            )}
          </>
        )}
        
        {/* Follow-up button visible after checkout too */}
        {appointment.status === 'checked_out' && (
          <>
            {!appointment.follow_up_appointment_id && (
              <Button 
                size="sm" 
                onClick={onFollowUp}
                data-testid={`followup-checkout-button-${appointment.id}`}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
              >
                <UserCheck className="w-4 h-4 mr-1" />
                Create Follow-up
              </Button>
            )}
            {appointment.follow_up_appointment_id && (
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => onViewLinkedAppointment(appointment.follow_up_appointment_id)}
                data-testid={`view-followup-checkout-${appointment.id}`}
                className="border-emerald-300 text-emerald-600 hover:bg-emerald-50 rounded-lg"
              >
                <UserCheck className="w-4 h-4 mr-1" />
                Follow-up Created →
              </Button>
            )}
          </>
        )}
        
        {/* Issue Token button - for queue-based appointments without token (allowed after check-in) */}
        {!appointment.token_number && appointment.service_type === 'queue_based' && appointment.status !== 'cancelled' && appointment.status !== 'forwarded' && appointment.status !== 'no_show' && appointment.status !== 'checked_out' && (
          <Button 
            size="sm" 
            onClick={onIssueToken}
            data-testid={`issue-token-button-${appointment.id}`}
            className="bg-purple-600 hover:bg-purple-700 text-white rounded-lg"
          >
            <Hash className="w-4 h-4 mr-1" />
            Issue Token
          </Button>
        )}

        {/* Package button - visible for all active appointments */}
        {!appointment.is_package && appointment.status !== 'cancelled' && appointment.status !== 'forwarded' && appointment.status !== 'no_show' && (
          <Button 
            size="sm" 
            variant="outline"
            onClick={onMarkPackage}
            data-testid={`mark-package-button-${appointment.id}`}
            className="border-indigo-300 text-indigo-600 hover:bg-indigo-50 rounded-lg"
          >
            <Package className="w-4 h-4 mr-1" />
            Package
          </Button>
        )}

        {/* Undo Package button - visible when package is marked */}
        {appointment.is_package && appointment.status !== 'cancelled' && appointment.status !== 'forwarded' && appointment.status !== 'no_show' && (
          <Button 
            size="sm" 
            variant="outline"
            onClick={onUndoPackage}
            data-testid={`undo-package-button-${appointment.id}`}
            className="border-amber-300 text-amber-600 hover:bg-amber-50 rounded-lg"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Undo Package
          </Button>
        )}
        
        {!isCheckedIn && appointment.status !== 'cancelled' && appointment.status !== 'forwarded' && appointment.status !== 'no_show' && appointment.status !== 'checked_out' && (
          <>
            <Button 
              size="sm" 
              variant="outline"
              onClick={onCancel}
              data-testid={`cancel-button-${appointment.id}`}
              className="border-red-300 text-red-600 hover:bg-red-50 rounded-lg"
            >
              <XCircle className="w-4 h-4 mr-1" />
              Cancel
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              onClick={onNoShow}
              data-testid={`no-show-button-${appointment.id}`}
              className="border-rose-300 text-rose-600 hover:bg-rose-50 rounded-lg"
            >
              No Show
            </Button>
          </>
        )}
        
        {!isCheckedIn && (
          <Button 
            size="sm" 
            variant="outline"
            onClick={onSendDirections}
            data-testid={`directions-button-${appointment.id}`}
            className="border-cyan-300 text-cyan-600 hover:bg-cyan-50 rounded-lg"
          >
            <MapPin className="w-4 h-4 mr-1" />
            Send Directions
          </Button>
        )}

        {/* Notes button - always visible for all statuses */}
        <Button 
          size="sm" 
          variant="outline"
          onClick={onAddNotes}
          data-testid={`notes-button-${appointment.id}`}
          className="border-blue-300 text-blue-600 hover:bg-blue-50 rounded-lg"
        >
          <FileText className="w-4 h-4 mr-1" />
          {appointment.remarks ? 'Edit Notes' : 'Add Notes'}
        </Button>
      </div>

      {/* Footer - Created/Updated timestamp (one line, first name only) */}
      {(appointment.last_updated_by_user_name || appointment.created_by_user_name) && (
        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500" data-testid="appointment-audit-footer">
          {appointment.last_updated_by_user_name ? (
            <>
              <span data-testid="appointment-updated-by">Last updated by: {appointment.last_updated_by_user_name.split(' ')[0]}</span>
              <span>{formatDateTime(appointment.updated_at)}</span>
            </>
          ) : (
            <>
              <span data-testid="appointment-created-by">Created by: {appointment.created_by_user_name.split(' ')[0]}</span>
              <span>{formatDateTime(appointment.created_at)}</span>
            </>
          )}
        </div>
      )}
    </div>
    </>
  );
};

export default AppointmentCard;
